import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, DoorOpen, AlertTriangle, MailCheck, Archive } from "lucide-react";

const pub = axios.create({ baseURL: (process.env.REACT_APP_BACKEND_URL || "") + "/api" });
const fmt = (s) => s ? new Date(s).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div data-testid="exit-confirm-page" className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="brand-grad px-6 py-4 flex items-center gap-2 text-white">
          <DoorOpen size={20} /><span className="font-bold text-lg">Kairos · Exit Confirmation</span>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export default function ExitConfirm({ token }) {
  const isSample = token === "sample-preview";
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (isSample) return;
    pub.get(`/exits/confirm/${token}`).then((r) => setCtx(r.data)).catch((e) => setErr(e?.response?.data?.detail || "This link is not valid"));
  }, [token, isSample]);

  const confirm = () => {
    setBusy(true); setErr(null);
    pub.post(`/exits/confirm/${token}`, { full_name: name, note })
      .then((r) => setDone(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not confirm"))
      .finally(() => setBusy(false));
  };

  if (isSample) return (
    <Shell>
      <div data-testid="exit-confirm-sample" className="text-center flex flex-col items-center gap-2">
        <div className="w-14 h-14 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mb-1"><MailCheck size={30} /></div>
        <p className="font-bold text-gray-800">This is just a sample email</p>
        <p className="text-sm text-gray-500">You opened the preview link from a test email, so there's nothing to confirm here. Real offboarding emails carry a unique link that opens the actual confirmation for that employee.</p>
      </div>
    </Shell>
  );
  if (err && !ctx) return <Shell><div className="text-center text-rose-600 flex flex-col items-center gap-2"><AlertTriangle size={28} /><p className="font-bold">{err}</p></div></Shell>;
  if (!ctx) return <Shell><div className="py-8 grid place-items-center text-gray-300"><Loader2 size={24} className="animate-spin" /></div></Shell>;
  if (ctx.purged || done?.purged) return (
    <Shell>
      <div data-testid="exit-purged" className="text-center flex flex-col items-center gap-2">
        <div className="w-14 h-14 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center mb-1"><Archive size={28} /></div>
        <p className="font-bold text-gray-800">This request has been purged</p>
        <p className="text-sm text-gray-500">There's nothing to action here — this exit was removed and no action is needed.</p>
      </div>
    </Shell>
  );

  const alreadyDone = done?.already_confirmed || done?.confirmed || ctx.already_confirmed;
  const who = done?.confirmed_by_name || ctx.confirmed_by_name;
  const at = done?.confirmed_at || ctx.confirmed_at;

  return (
    <Shell>
      <div className="text-sm text-gray-500 mb-1">Offboarding for</div>
      <div className="text-xl font-bold text-gray-900">{ctx.employee_name}{ctx.location ? <span className="text-sm font-normal text-gray-400"> · {ctx.location}</span> : null}</div>
      <div className="mt-3 rounded-xl bg-indigo-50 border border-indigo-100 px-3 py-2.5">
        <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-500">Your part</div>
        <div className="font-bold text-gray-800">{ctx.section_name}</div>
        {ctx.action && <div className="text-sm text-gray-600">{ctx.action}</div>}
      </div>

      {alreadyDone ? (
        <div data-testid="exit-confirm-done" className="mt-5 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-2"><CheckCircle2 size={30} /></div>
          <p className="font-bold text-emerald-700">This part is confirmed</p>
          <p className="text-sm text-gray-500 mt-0.5">Marked done by <b>{who}</b>{at ? ` on ${fmt(at)}` : ""}.</p>
          {(done?.note) && <p className="text-sm text-gray-400 italic mt-1">“{done.note}”</p>}
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {ctx.needs_name && (
            <label className="block"><span className="text-xs font-bold text-gray-500">Your full name *</span>
              <input data-testid="confirm-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="So we know who confirmed"
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300" /></label>
          )}
          <label className="block"><span className="text-xs font-bold text-gray-500">Note (optional)</span>
            <input data-testid="confirm-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300" /></label>
          {err && <div className="text-sm text-rose-600">{err}</div>}
          <button data-testid="confirm-btn" disabled={busy || (ctx.needs_name && !name.trim())} onClick={confirm}
            className="w-full h-11 rounded-xl brand-grad text-white font-bold disabled:opacity-40 hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={18} />} I've done my part
          </button>
        </div>
      )}
      <div className="mt-5 pt-4 border-t border-gray-100 text-center">
        <a data-testid="confirm-view-status" href={`/exit-status/${token}`}
          className="text-xs font-bold text-indigo-600 hover:text-indigo-800">View request status →</a>
      </div>
    </Shell>
  );
}
